import React, { useState, useRef, useEffect, useMemo, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { Column } from "primereact/column";
import { useGeneratePaymentOrderColumns, generateSubGridInvoiceColumns } from "./generatePaymentOrderColumns";
import { Checkbox } from "primereact/checkbox";
import { toast } from "react-toastify";
import moment from "moment";
import { useAppContext, usePersistedState } from "../../../AppContext";
import mainAxios from "../../../services/mainAxios";
import Loader from "../../Layout/Loader";
import PrimeTable from "../../PrimeTable/PrimeTable";
import DateRange from "../../global/DateRange";
import { Link } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlus, faFilter, faChevronUp } from "@fortawesome/free-solid-svg-icons";
import ExportCsvMultiple from "../../global/ExporCsvMultiple";
import usePaymentOrderStatistics from "./usePaymentOrderStatistics";
import { globalFilterStatisticCard } from "../../global/StatisticsCards";

const PaymentsOrder = () => {
  const { t, i18n } = useTranslation("translations");
  const [paymentOrders, setPaymentOrders] = useState(null);
  const [selectedOrders, setSelectedOrders] = useState([]);
  const [actionLoading, setActionLoading] = useState(false);

  const {
    user: [user],
    selectedCompany: [selectedCompany],
    company: [fullCompany],
    dates: [dates, setDates],
    reloadData: [reloadData]
  } = useAppContext();

  const company = user.companiesRights.find(
    (company) => company.id === parseInt(selectedCompany)
  );

  const [loading, setLoading] = useState(false);
  const [filtersCollapsed, setFiltersCollapsed] = usePersistedState(false, "paymentOrdersFiltersCollapsed")

  const [expanded, setExpanded] = useState({
    relatedInvoices: [],
    expandedRows: null,
  });

  const defaultExpand = {
    relatedInvoices: [],
    expandedRows: null,
  };


  const filterParams = {
  };

  const defaultFilters = {
    fromDate: dates.fromDate,
    toDate: dates.toDate,
    params: {
      ...filterParams,
    },
  };
  const [filters, setFilters] = useState(defaultFilters);

  const filterCount = useMemo(() => {
    let count = 0;
    const params = filters?.params;
    if (defaultFilters && params) {
      Object.keys(params).forEach(key => {
        const value = params[key];
        const isDefault = JSON.stringify(value) === JSON.stringify(defaultFilters?.params?.[key]);
        if (!isDefault && value != null) {
          Array.isArray(value) ? count += value.length : count++;
        }
      });
    }
    return count;
  }, [defaultFilters, filters]);

  const  {profiscStatusOptions,bankStatusOptions,currencyOptions,refreshStatistics} = usePaymentOrderStatistics({filters})

  React.useEffect(() => {
    getFilteredResults({
      data: { ...defaultFilters, params: { ...defaultFilters.params } },
      pageSize: 10,
      page: 1
    });
  }, [selectedCompany, reloadData]);
  
  const defaultSortCriteria = {
    field: "recordDate",
    direction: "DESC",
  };

  const [paymentOrderForCsv, setpaymentOrderForCsv] = useState([]);
  const [tableRows, setTableRows] = useState({
    totalRows: 0,
    rowsPerPage: 10,
    currentPage: 1,
    sortCriteria: defaultSortCriteria,

  });



  const getFilteredResults = ({
    data,
    pageSize = 10,
    page = 1,
    clearFilters = false,
    getPaymentOrderForCsv = false,
    sortCriteria = [defaultSortCriteria],
  } = {}) => {
    const requestFilters = data ? data : filters;

    let requestBody = {
      object: "GetPaymentOrders",
      fromDate: filters.fromDate,
      toDate: filters.toDate,
      ...requestFilters,
      params: {
        ...requestFilters.params
      },
      pagination: getPaymentOrderForCsv
        ? null
        : {
            pageSize,
            pageNumber: page,
          },
      sortCriteria
    };

    return mainAxios
      .post(`/payment-orders/search`, requestBody)
      .then((res) => {
        setLoading(false);
        if (res?.data?.status === true) {
          if(getPaymentOrderForCsv){
            setpaymentOrderForCsv(res.data.content);
          }else{
            setPaymentOrders(res.data.content);
            setTableRows((tableRows) => ({
              ...tableRows,
              totalRows: res?.data?.totalSize,
            }));
          }

          return true;
        }
      })
      .catch((err) => {
        setLoading(false);
        return null;
      });
  };

  const handleParamsChange = (e, label, val) => {
    const key = e ? e.target.name : label;
    const value = e ? e.target.value : val;
    setFilters((filters) => ({
      ...filters,
      params: {
        ...filters.params,
        [key]: value,
      },
    }));
  };

  let columns = useGeneratePaymentOrderColumns({
    t,
    i18n,
    getFilteredResults,
    tableRows,
    filters,
    setFilters,
    refreshStatistics
  });

  const [selectedColumns, setSelectedColumns] = usePersistedState(
    columns.map((col) => ({
      field: col.field,
      show: !!col.default,
      ...(col?.persisted ? { persisted: true } : {}),
    })),
    "paymentOrderCols4"
  );

  const relatedInvoiceColumns = generateSubGridInvoiceColumns({ t,paymentOrder:expanded?.expandedRows?.[0] });
  const onRowCollapse = () => {
    setExpanded(defaultExpand);
  };

  const rowExpansionTemplate = () => {
    return (
      <div className="d-flex flex-column ml-3 mt-3" >
        <div className="mb-5" style={{width:"120vw"}}>
          <h4>{t("openBanking.relatedInv")}</h4>
          <PrimeTable
            value={expanded.relatedInvoices ?? []}
            columns={relatedInvoiceColumns}
            showFilters={false}
            paginator={false}
            filterDisplay={false}
            responsiveLayout="scroll"
          />
        </div>
      </div>
    );
  };
  const relatedInvoiceOnExpand = (event) => {
    setExpanded((state) => ({
      ...state,
      expandedRows: [event.data],
      relatedInvoices: event?.data?.paymentOrderRecords ?? [],
    }));
  };

  const onRowExpand = (event) => {
    relatedInvoiceOnExpand(event);
  };


  const sendMultiplePaymentOrders = async () => {
    setActionLoading(true);
    try {
      const response = await mainAxios.post(
        "/payment/send-multiple-payment-order",
        {
          content: selectedOrders.map((order) => order.id),
          operation: "received",
          username: user.username,
          nuis: user.companiesRights.find(
            (company) => company.id === parseInt(selectedCompany)
        )?.nuis,
        }
      );

      if (response.data.status) {
        toast.success("Multiple payment orders sent successfully!");
        setSelectedOrders([]);
        getFilteredResults({ data: filters, pageSize: 10, page: 1 });
      } else {
        toast.error("Failed to send multiple payment orders.");
      }
    } catch (error) {
      toast.error("Error sending multiple payment orders.");
    } finally {
      setActionLoading(false);
    }
  };

  const hasAcceptedOrders = selectedOrders.some(order => order.status.name === "accepted-customer-profile-2");

  const handleRowSelect = (event) => {
    const rowData = event.data;
    if (rowData.status.name === "accepted-customer-profile-2") {
      setSelectedOrders((prevSelected) =>
        prevSelected.includes(rowData)
          ? prevSelected.filter((order) => order !== rowData)
          : [...prevSelected, rowData]
      );
    }
  };

  const multiSelectBody = (rowData) =>
    rowData?.status?.name === "accepted-customer-profile-2" ? (
      <Checkbox
        onChange={() => handleRowSelect({ data: rowData })}
        checked={selectedOrders.includes(rowData)}
      />
    ) : null;
    const headerButtonsRight = hasAcceptedOrders ? (
      <div className="d-flex align-items-center mt-4">
    <button
    onClick={sendMultiplePaymentOrders}
    disabled={actionLoading}
    className="btn btn-sm btn-outline-primary mr-3"
  >
    {actionLoading ? "Sending..." : "Send Multiple Payments"}
  </button>
  </div>
    ) : null

  const sentMultiPaymentFilter = useMemo(() => {
        const invoicesForSentPayment = paymentOrders?.filter(rowData => rowData?.status?.name === "accepted-customer-profile-2") ?? []
        if (invoicesForSentPayment?.length == 0) return <></>
        return <Checkbox
            onChange={
                (e) => {
                    if (e.checked) {
                        const _selectedRows = []
                        invoicesForSentPayment.forEach(payment => _selectedRows.push(payment) )
                        setSelectedOrders(_selectedRows)
                    } else {
                        setSelectedOrders([])
                    }
                }
            }
            checked={selectedOrders.length == invoicesForSentPayment?.length && selectedOrders.length!=0}
        />
    }, [paymentOrders, selectedOrders])


    const filterStatisticCard = (typeOptions, props) =>
    globalFilterStatisticCard(typeOptions, {
      ...props,
      handleParamsChange,
      getFilteredResults,
      user,
      filters,
    });

  // Export dropdown for headerIcons
  const exportDropdown = (
    <ExportCsvMultiple
      data={paymentOrderForCsv}
      selectedColumns={selectedColumns}
      columns={columns}
      onClick={() => getFilteredResults({ getPaymentOrderForCsv: true })}
      excludedColumns={["status", "actions", "print"]}
      asDropdown={true}
    />
  );

  return paymentOrders ? (
    <>
      <div className="d-flex flex-wrap justify-content-between align-items-center my-2">
        <div className="left-header d-flex align-items-center">
          <h3 className="mb-0 p-0">{t("navbar.paymentOrder")}</h3>
          {[1, 2, 3, 4, 5].includes(user.role.id) && (
            <Link
                to={"/create-payment-order"}
                className="btn btn-sm btn-primary ml-3"
            >
                <FontAwesomeIcon icon={faPlus} className="mr-2" />
                {t("navbar.create")}
            </Link>
          )}
        </div>
        <div className="mb-4 mb-md-0">
          <DateRange
            fromDate={filters.fromDate}
            toDate={filters.toDate}
            onChange={(dates) => {
              let fromDate = dates[0];
              let toDate = dates[1];
              if (fromDate && toDate) {
                getFilteredResults({
                  data: {
                    ...filters,
                    fromDate,
                    toDate,
                    params: { ...filters.params },
                  },
                });
              }

              setFilters((filters) => ({
                ...defaultFilters,
                fromDate,
                toDate,
              }));
              setDates({ fromDate, toDate });
            }}
          />
        </div>
      </div>
      <div className="filter-cards-container d-none d-md-block">
        <div className="filter-cards-header">
          <button
            className={`filter-cards-toggle ${filtersCollapsed ? 'filter-cards-toggle--collapsed' : ''}`}
            onClick={() => setFiltersCollapsed(!filtersCollapsed)}
          >
            <FontAwesomeIcon icon={faFilter} />
            {filtersCollapsed ? (t("common.showFilters", "Show Filters")) : (t("common.hideFilters", "Hide Filters"))}
            {filterCount > 0 && <span className="badge badge-primary ml-1">{filterCount}</span>}
            <FontAwesomeIcon icon={faChevronUp} />
          </button>
        </div>
        <div className={`filter-cards-row ${filtersCollapsed ? 'filter-cards-row--collapsed' : ''}`}>
          <div className="filter-card-col">
            {filterStatisticCard(profiscStatusOptions, {
              title: t("openBanking.profiscStatus"),
              cardClasses: "stats-cols-2",
            })}
          </div>
          <div className="filter-card-col">
            {filterStatisticCard(bankStatusOptions, {
              title: t("openBanking.bankStatus"),
              cardClasses: "stats-cols-2",
            })}
          </div>
          <div className="filter-card-col">
            {filterStatisticCard(currencyOptions, {
              title: t("invoice.currency"),
              cardClasses: "stats-cols-1",
            })}
          </div>
        </div>
      </div>
      <div>
            <PrimeTable
              showFilters={true}
              reorderableColumns={true}
              filters={[filters, setFilters]}
              onFilter={(data) => {
                setFilters(data.filters);
              }}
              defaultFilters={defaultFilters}
              tableRows={[tableRows, setTableRows]}
              value={paymentOrders}
              gridKey="paymentOrderCols4"
              selectedColumns={[selectedColumns, setSelectedColumns]}
              searchableKeys={["buyerName", "ublCustomerCityName", "buyerNuis"]}
              columns={columns}
              expandedRows={expanded?.expandedRows ?? []}
              onRowExpand={onRowExpand}
              onRowCollapse={onRowCollapse}
              headerButtonsRight={headerButtonsRight}
              headerIcons={exportDropdown}
              rowExpansionTemplate={rowExpansionTemplate}
              dataKey="ublId"
              onRemoteDataChange={(remoteData) => {
                const updatedFilters = remoteData.filters ?? filters;
                const updatedTableData = remoteData.tableRows ?? tableRows;
                let { currentPage, rowsPerPage, sortCriteria } = updatedTableData;
                sortCriteria = sortCriteria ? [sortCriteria] : [];
                getFilteredResults({
                  data: updatedFilters,
                  pageSize: rowsPerPage,
                  page: currentPage,
                  sortCriteria,
                });
              }}
              defaultSortCriteria={defaultSortCriteria}
              onRowSelect={handleRowSelect}
              onClearFilter={() => {
                getFilteredResults({
                  data: { ...filters, params: { ...defaultFilters.params } },
                  pageSize: 10,
                  rowsPerPage: 1,
                  clearFilters: true,
                });
              }}
              columnDeps={[expanded, fullCompany]}
              loading={loading}
              mobileFilterContent={
                <div className="filter-cards-row">
                  <div className="filter-card-col">
                    {filterStatisticCard(profiscStatusOptions, {
                      title: t("openBanking.profiscStatus"),
                      cardClasses: "stats-cols-2",
                    })}
                  </div>
                  <div className="filter-card-col">
                    {filterStatisticCard(bankStatusOptions, {
                      title: t("openBanking.bankStatus"),
                      cardClasses: "stats-cols-2",
                    })}
                  </div>
                  <div className="filter-card-col">
                    {filterStatisticCard(currencyOptions, {
                      title: t("invoice.currency"),
                      cardClasses: "stats-cols-1",
                    })}
                  </div>
                </div>
              }
            >
              <Column
                body={multiSelectBody}
                style={{ width: "3em" }}
                filterElement={sentMultiPaymentFilter}
                filter={true}
                showFilterMenu={false}
                showClearButton={false}
              />
              <Column
                expander={() => true}
                style={{ width: "0.5em" }}
                header=""
                field="expansion"
                frozen={false}
              />
              
            </PrimeTable>
      </div>
    </>
  ) : (
    <Loader />
  );
};

export default PaymentsOrder;

